import { useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router'
import { motion } from 'framer-motion'
import { Shield } from 'lucide-react'
import SignInForm from '../components/auth/SignInForm'
import { consumeAuthReturnTo, normalizeReturnTo, persistAuthReturnTo } from '../lib/authReturnTo'
import { useSEO } from '../hooks/useSEO'
import { withMarketingQuery } from '../utils/marketingLinks'
import { getPostAuthDestination } from '../lib/postAuthRouting'

export default function SignInPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  useSEO('/signin')
  const returnTo = normalizeReturnTo(searchParams.get('returnTo') || searchParams.get('redirect'))

  useEffect(() => {
    persistAuthReturnTo(returnTo)
  }, [returnTo])

  const handleSuccess = () => {
    const storedReturnTo = consumeAuthReturnTo()
    if (storedReturnTo) {
      navigate(storedReturnTo)
      return
    }

    navigate(getPostAuthDestination(), { replace: true })
  }

  return (
    <div className="auth-native-page flex min-h-screen bg-white dark:bg-slate-950">
      {/* Left Side - Branding and Visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-950 dark:bg-slate-900 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-grid-pattern bg-[length:30px_30px] opacity-[0.1]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-brand-950/50 dark:to-slate-900/50"></div>

        {/* Amber glow for dark mode */}
        <div className="absolute top-1/4 -right-20 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] hidden dark:block"></div>

        <div className="relative flex flex-col justify-center px-12 py-12 w-full max-w-2xl mx-auto z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Logo/Brand */}
            <Link to="/" className="inline-block mb-12">
              <span className="text-3xl font-bold text-white font-display">Optioneer</span>
            </Link>

            {/* Headline */}
            <h1 className="text-4xl font-bold text-white mb-6 font-display leading-tight">
              Welcome Back to
              <br />
              Optioneer
            </h1>

            <p className="text-xl text-brand-100/80 dark:text-slate-400 mb-12 font-light">
              Continue managing your options portfolio with clarity and confidence.
            </p>

            {/* Visual Element - Mini Dashboard Preview */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="rounded-2xl overflow-hidden border border-brand-700/50 dark:border-slate-700/50 shadow-2xl shadow-brand-900/50 dark:shadow-black/50"
            >
              <div className="aspect-video bg-brand-950 dark:bg-slate-900 p-4 relative overflow-hidden">
                {/* Background Grid */}
                <div className="absolute inset-0 bg-grid-pattern bg-[length:20px_20px] opacity-[0.05]"></div>

                {/* Mini Dashboard */}
                <div className="relative h-full flex flex-col gap-2">
                  {/* Header with Live Badge */}
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold text-brand-300 dark:text-slate-400">
                      Dashboard
                    </div>
                    <div className="flex items-center gap-1 bg-brand-800/50 dark:bg-slate-800/50 backdrop-blur-sm rounded px-2 py-0.5 border border-brand-700/50 dark:border-slate-700/50">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 dark:bg-amber-400 animate-pulse"></div>
                      <span className="text-[8px] font-mono text-brand-200 dark:text-slate-300">
                        LIVE
                      </span>
                    </div>
                  </div>

                  {/* Metric Cards */}
                  <div className="grid grid-cols-4 gap-1.5 flex-1">
                    <div className="bg-brand-900/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-lg border border-brand-800/50 dark:border-slate-700/50 p-2 flex flex-col justify-between">
                      <div className="text-[7px] text-brand-400 dark:text-slate-500">Total P&L</div>
                      <div className="text-[11px] font-bold text-emerald-400 dark:text-amber-400">
                        +$8.4k
                      </div>
                    </div>
                    <div className="bg-brand-900/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-lg border border-brand-800/50 dark:border-slate-700/50 p-2 flex flex-col justify-between">
                      <div className="text-[7px] text-brand-400 dark:text-slate-500">
                        Unrealized
                      </div>
                      <div className="text-[11px] font-bold text-emerald-400 dark:text-amber-400">
                        +$3.2k
                      </div>
                    </div>
                    <div className="bg-brand-900/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-lg border border-brand-800/50 dark:border-slate-700/50 p-2 flex flex-col justify-between">
                      <div className="text-[7px] text-brand-400 dark:text-slate-500">Realized</div>
                      <div className="text-[11px] font-bold text-emerald-400 dark:text-amber-400">
                        +$5.2k
                      </div>
                    </div>
                    <div className="bg-brand-900/50 dark:bg-slate-800/50 backdrop-blur-sm rounded-lg border border-brand-800/50 dark:border-slate-700/50 p-2 flex flex-col justify-between">
                      <div className="text-[7px] text-brand-400 dark:text-slate-500">Avg ROI</div>
                      <div className="text-[11px] font-bold text-emerald-400 dark:text-amber-400">
                        +18%
                      </div>
                    </div>
                  </div>

                  {/* Chart Area */}
                  <div className="flex-1 bg-brand-900/30 dark:bg-slate-800/30 backdrop-blur-sm rounded-lg border border-brand-800/50 dark:border-slate-700/50 p-2 relative overflow-hidden">
                    {/* Simple ascending line */}
                    <svg
                      className="w-full h-full opacity-60"
                      viewBox="0 0 100 40"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M 0 35 L 20 30 L 40 25 L 60 22 L 80 15 L 100 8"
                        fill="none"
                        className="stroke-emerald-500 dark:stroke-amber-400"
                        strokeWidth="1.5"
                        vectorEffect="non-scaling-stroke"
                      />
                      <path
                        d="M 0 35 L 20 30 L 40 25 L 60 22 L 80 15 L 100 8 L 100 40 L 0 40 Z"
                        fill="url(#gradient)"
                        opacity="0.3"
                      />
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop
                            offset="0%"
                            className="[stop-color:#10b981] dark:[stop-color:#f59e0b]"
                            stopOpacity="0.5"
                          />
                          <stop
                            offset="100%"
                            className="[stop-color:#10b981] dark:[stop-color:#f59e0b]"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-native-content flex w-full items-center justify-center bg-white px-6 py-12 pt-28 dark:bg-slate-950 sm:pt-12 lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Form Container */}
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 sm:p-10 shadow-xl shadow-brand-900/5 dark:shadow-black/20 border border-slate-100 dark:border-slate-800">
            {/* Mobile Logo */}
            <div className="lg:hidden mb-8 text-center">
              <Link to="/" className="inline-block">
                <span className="text-2xl font-bold text-brand-600 dark:text-amber-400 font-display">
                  Optioneer
                </span>
              </Link>
            </div>

            <h1 className="mb-2 text-3xl font-bold text-slate-900 dark:text-white font-display">
              Sign in
            </h1>
            <p className="mb-8 text-slate-500 dark:text-slate-400">
              Welcome back! Please enter your details.
            </p>

            <SignInForm onSuccess={handleSuccess} />

            {/* Sign Up Link */}
            <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
              Don't have an account?{' '}
              <Link
                to={withMarketingQuery(
                  returnTo ? `/signup?returnTo=${encodeURIComponent(returnTo)}` : '/signup',
                  searchParams.toString()
                )}
                className="font-semibold text-brand-600 dark:text-amber-400 hover:text-brand-700 dark:hover:text-amber-300"
              >
                Start 7-Day Premium Trial
              </Link>
            </p>
          </div>

          {/* Trust Elements */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 dark:text-slate-500">
              <Shield className="h-4 w-4" />
              <span>Secure login with bank-level encryption</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
